import { Article } from '../../components/Layout'
import { H1, H2, P, Lead, Steps, Step, Callout, Code, CodeBlock, Screenshot } from '../../components/DocComponents'

const toc = [
  { id: 'goal', title: 'What we will build', level: 2 as const },
  { id: 'step1', title: '1 · Register & log in', level: 2 as const },
  { id: 'step2', title: '2 · Model your data', level: 2 as const },
  { id: 'step3', title: '3 · Create a Preparation', level: 2 as const },
  { id: 'step4', title: '4 · Build a Plate', level: 2 as const },
  { id: 'step5', title: '5 · Deploy & view', level: 2 as const },
]

export function QuickStart() {
  return (
    <Article toc={toc}>
      <H1>Quick Start</H1>
      <Lead>
        In this guide you will go from a blank Rabbit installation to a working data-entry workflow
        with a live dashboard — in about ten minutes.
      </Lead>

      <Callout type="info" title="Prerequisites">
        All Rabbit services must be running. Start them from the project root or follow your team's
        deployment instructions. The Console is at <Code>http://localhost:3004</Code>.
      </Callout>

      <H2 id="goal">What we will build</H2>
      <P>
        A minimal <strong>"Product Catalogue"</strong> system:
      </P>
      <Steps>
        <Step n={1} title="Register & log in">A business account and the first admin user.</Step>
        <Step n={2} title="Model your data">A <em>Product</em> cup with Name, SKU, and Price traits.</Step>
        <Step n={3} title="Create a Preparation">A guided data-entry workflow to add new products.</Step>
        <Step n={4} title="Build a Plate">A dashboard showing all products and a button to add more.</Step>
        <Step n={5} title="Deploy & view">Make the plate live and open it.</Step>
      </Steps>

      <H2 id="step1">Step 1 · Register & log in</H2>
      <Steps>
        <Step n={1} title="Open the Console">
          Navigate to <Code>http://localhost:3004</Code>. Click <strong>Register</strong> on the login screen.
        </Step>
        <Step n={2} title="Create your business">
          Fill in a <strong>Business name</strong>, your <strong>Full name</strong>, <strong>Email</strong>, and a
          strong <strong>Password</strong>. Submit the form.
          <Screenshot alt="Registration form with business name, full name, email, and password fields" caption="The registration screen creates both a business and its first admin user." />
        </Step>
        <Step n={3} title="Verify your email">
          Check your inbox for a verification link and click it. You will be redirected back to the Console.
        </Step>
        <Step n={4} title="Log in">
          Use the credentials you just created. You land on the main Console dashboard.
        </Step>
      </Steps>

      <H2 id="step2">Step 2 · Model your data</H2>
      <P>
        First create the traits (field definitions), then the cup (entity type) that uses them.
      </P>
      <Steps>
        <Step n={1} title="Create Traits">
          Go to <strong>Brainer › Traits</strong> and add three traits:
          <ul className="list-disc pl-4 mt-1 space-y-0.5">
            <li><Code>Name</Code> — type <Code>text</Code>, required</li>
            <li><Code>SKU</Code> — type <Code>text</Code>, identifier, required</li>
            <li><Code>Price</Code> — type <Code>number</Code></li>
          </ul>
        </Step>
        <Step n={2} title="Create the Product Cup">
          Go to <strong>Brainer › Cups</strong> → <strong>New Cup</strong>. Name it <Code>Product</Code>.
        </Step>
        <Step n={3} title="Add Traits to the Cup">
          Open the <em>Product</em> cup detail page and click <strong>Add trait</strong>. Add <Code>Name</Code>,
          <Code>SKU</Code>, and <Code>Price</Code> in order.
          <Screenshot alt="Cup detail page showing the traits panel with Name, SKU, and Price added" caption="Cup traits define the columns/fields for all records of this type." />
        </Step>
      </Steps>

      <H2 id="step3">Step 3 · Create a Preparation</H2>
      <Steps>
        <Step n={1} title="Open Preparations">
          Go to <strong>Brainer › Preparations</strong> → <strong>New Preparation</strong>. Name it
          <Code>Add Product</Code>.
        </Step>
        <Step n={2} title="Add a Step">
          Inside the preparation detail, click <strong>Add Step</strong>. Choose type <strong>Interviewer</strong>.
          This creates a guided Q&A that collects field values from the user.
        </Step>
        <Step n={3} title="Configure the Interviewer">
          In the step wizard:
          <ul className="list-disc pl-4 mt-1 space-y-0.5">
            <li>Set <strong>Target cup</strong> to <Code>Product</Code></li>
            <li>Enable <strong>Create new record</strong></li>
            <li>Add three items: one for each trait (Name, SKU, Price)</li>
          </ul>
        </Step>
        <Step n={4} title="Save and verify">
          Save the step. The preparation is now ready to run — each time it is triggered, it walks
          the user through entering a new product.
        </Step>
      </Steps>
      <Callout type="tip">
        Preparations can have many steps in sequence. Steps can be automated (no user input),
        generate data via Drinkers, or branch conditionally using Funnel connectors.
      </Callout>

      <H2 id="step4">Step 4 · Build a Plate</H2>
      <Steps>
        <Step n={1} title="Create a Plate">
          Go to <strong>Brainer › Plates</strong> → <strong>New Plate</strong>. Name it <Code>Product Catalogue</Code>.
        </Step>
        <Step n={2} title="Add an Input Parameter">
          In the plate detail, go to the <strong>Parameters</strong> tab → <strong>Add parameter</strong>.
          Add <Code>cupId</Code> of type <Code>uuid</Code> with a <strong>fixed value</strong> set to the
          UUID of your <em>Product</em> cup. This tells the plate which cup to display.
        </Step>
        <Step n={3} title="Add a Section">
          On the <strong>Builder</strong> tab, click <strong>Add section</strong>. Name it
          <Code>All Products</Code>, width <Code>100%</Code>.
        </Step>
        <Step n={4} title="Add a View">
          Inside that section, click <strong>Add view</strong>. In the wizard:
          <ul className="list-disc pl-4 mt-1 space-y-0.5">
            <li>Name: <Code>Products</Code>, category: <strong>Table</strong></li>
            <li>Source: <strong>Coffee API</strong>, mode: <strong>Cup list</strong></li>
            <li>Input param: <Code>cupId</Code></li>
            <li>Discover columns → enable Name, SKU, Price</li>
          </ul>
          <Screenshot alt="View wizard showing the Coffee API tab with cup_list mode and column selection" caption="The discovery step fetches a sample row to auto-detect columns." />
        </Step>
        <Step n={5} title="Add a button view">
          Add a second view in the same section, category <strong>Button</strong>. Set label to
          <Code>Add Product</Code> and link it to a plate that runs the <em>Add Product</em> preparation.
        </Step>
      </Steps>

      <H2 id="step5">Step 5 · Deploy & view</H2>
      <Steps>
        <Step n={1} title="Verify the plate">
          Click <strong>Verify</strong>. Rabbit checks that all views are correctly configured.
        </Step>
        <Step n={2} title="Deploy">
          Click <strong>Deploy</strong>. The plate status changes to <Code>deployed</Code>.
        </Step>
        <Step n={3} title="Open the plate runner">
          Click the <strong>▶ Run</strong> button on the plate card. The plate renders with your
          product list table (empty for now) and the <em>Add Product</em> button.
        </Step>
        <Step n={4} title="Add your first product">
          Click <strong>Add Product</strong>, fill in the interviewer, and submit. Return to the plate —
          your new product appears in the table.
          <Screenshot alt="Plate runner showing a table with one product row and an Add Product button" caption="The live plate updates automatically when new records are added." />
        </Step>
      </Steps>

      <Callout type="tip" title="Next steps">
        Now that you have the basics, explore <a href="/docs/brainer/cups" className="text-blue-600 underline underline-offset-2">Cups in depth</a>,
        learn about <a href="/docs/plates/filters" className="text-blue-600 underline underline-offset-2">filtering and ordering</a>,
        or publish your plate to <a href="/docs/plates/openplace" className="text-blue-600 underline underline-offset-2">OpenPlace</a> so anyone can access it without logging in.
      </Callout>
    </Article>
  )
}
